// ─────────────────────────────────────────────────────────────────
// Phase B.1.a — Service IA suggestion d'analyses Ortho/MGD
// Date : 2026-05-09
//
// Role : a partir d'une anamnese pseudonymisee + pack achete + catalogue
// lab_tests, propose une liste de 3-5 analyses pertinentes avec
// justification clinique courte et score de pertinence.
//
// L'IA PROPOSE, Anissa DECIDE. Anissa coche/decoche dans la modale,
// puis valide pour creer un analysis_plan en BDD.
//
// Contraintes :
// - Pseudonymisation systematique avant envoi a Claude (jamais nom/email/phone)
// - Output JSON strict, valide via safeParseJson
// - Garde-fous prompt : pas de diagnostic, pas de medicament, pas plus de
//   50% du budget pack en analyses sauf justifie cliniquement
// ─────────────────────────────────────────────────────────────────

import { callClaude, ClaudeApiError } from './anthropic';
import { stripAdministrativePromptFields } from './promptFormPrivacy';

const MODEL = 'claude-sonnet-4-6';  // Sonnet pour qualite raisonnement clinique
const MAX_TOKENS = 2000;

/**
 * Pseudonymise une anamnese cliente pour l'envoi a Claude.
 * Retire toutes les donnees identifiantes (nom, email, phone, date de
 * naissance complete, adresse). Conserve le contenu clinique pertinent.
 */
export function pseudonymizeAnamnesis(client) {
  const form = stripAdministrativePromptFields(client?.form || {});

  // Age range (privacy-preserving, 5-year bucket).
  // V97.11.9 : fallback sur form.age si dateNaissance manquante (le pre-q
  // V97.8.1 ne demande que l'age, pas la date de naissance complete).
  // Sinon l'algo voit age_range=null + anamnese.age="38" et signale une
  // incoherence injustifiee.
  const ageRange = (() => {
    const naissance = form.dateNaissance || form.date_naissance || null;
    let ageNow = null;
    if (naissance) {
      const birth = new Date(naissance);
      if (!isNaN(birth)) ageNow = new Date().getFullYear() - birth.getFullYear();
    }
    if (ageNow === null && form.age) {
      const n = parseInt(form.age, 10);
      if (!isNaN(n) && n > 0 && n < 130) ageNow = n;
    }
    if (ageNow === null) return null;
    const lo = Math.floor(ageNow / 5) * 5;
    return `${lo}-${lo + 4}`;
  })();

  // V97.11.8 : avant ce fix, on avait un mapping hardcode de 14 champs avec
  // des noms qui ne matchaient pas la vraie forme (sexe vs genre, digestion vs
  // ressentiDigestion, niveau_stress vs niveauStressActuel, etc.) — la plupart
  // des champs sortaient null. En plus, "form.grossesseActuelle = 'Non'" etait
  // truthy donc on construisait "trimestre undefined", d'ou l'alerte algo
  // "renseignes comme 'undefined'".
  //
  // Nouvelle approche : on envoie TOUT le form (filtre PII strict + filtre
  // valeurs vides) a l'IA. Aucun mapping a maintenir, robuste a tout nouveau
  // champ ajoute au formulaire SaaS.
  const PII_FIELDS = new Set([
    'prenom', 'nom', 'email', 'telephone', 'adresse',
    'dateNaissance', 'date_naissance', 'medecinTraitant',
  ]);
  const ADMIN_FIELDS = new Set([
    'packtype', 'pack', 'formule', 'price', 'prix', 'credit', 'credits',
    'analysescredit', 'analysiscredit', 'appmode',
  ]);

  const pseudoForm = {};
  for (const [key, value] of Object.entries(form)) {
    if (PII_FIELDS.has(key)) continue;
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (ADMIN_FIELDS.has(normalizedKey)) continue;
    if (value === '' || value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    pseudoForm[key] = value;
  }

  return {
    client_pseudo_id: client?.id ? String(client.id).slice(0, 8) : 'C-anon',
    age_range: ageRange,
    anamnese: pseudoForm,
  };
}

/**
 * Build le system prompt pour l'IA suggestion d'analyses.
 */
function buildSystemPrompt() {
  return `Tu es l'assistant clinique d'Anissa Deroubaix, nutritionniste fonctionnelle a Nyon (Suisse).

Ta mission : analyser une anamnese pseudonymisee et proposer 0 a 3 analyses biologiques pertinentes parmi le catalogue Ortho-Analytic + MGD fourni.

Tu PROPOSES, Anissa DECIDE. Tu ne prescris jamais directement.

REGLE DE FOND : la pertinence prime sur le volume. Si l'anamnese ne justifie pas
d'analyse particuliere, propose-en aucune et explique pourquoi dans client_summary.
Mieux vaut 1 analyse vraiment pertinente que 5 qui ressemblent a un catalogue.

NOMBRE D'ANALYSES :
- 0 si l'anamnese ne justifie pas (rare, mais possible : profil clair, symptomes mineurs)
- 1-2 si terrain bien identifie avec axe clair
- 3 maximum si profil complexe avec plusieurs axes a explorer en parallele
- JAMAIS plus de 3, meme si pack premium

Pour chaque analyse proposee :
- Justification clinique courte (1 phrase, ancree dans l'anamnese)
- pertinence_score : 1-10 (mapping affichage cote UI : >=10 "Pertinence elevee",
  7-9 "Pertinence moderee", <=6 "Pertinence exploratoire")
- recommended_default : toujours false. Anissa choisit chaque analyse.

VOCABULAIRE OBLIGATOIRE (ton prudent juridiquement) :
- "compatible avec un terrain... fonctionnel"
- "tendance evoquant..."
- "contexte coherent avec..."
- "axe a explorer..."
- "signaux fonctionnels justifiant une exploration X medicale"
- "hypothese fonctionnelle a investiguer..."
- "piste a creuser..."

VOCABULAIRE INTERDIT (impression diagnostique) :
- "detecte / indique / confirme / presence de"
- "suggere fortement une pathologie"
- "la cliente a [maladie]"
- "souffre de"
- nom de pathologie suivi de "diagnostique" ou "probable"
- "terrain thyroidien" / "terrain hormonal" / "terrain inflammatoire"
  utilises seuls sans qualifier "fonctionnel" ou "exploration medicale" :
  ces formulations s'approchent d'un langage quasi-diagnostique. Toujours
  qualifier : "signaux fonctionnels evoquant un axe thyroidien a explorer
  medicalement" plutot que "terrain thyroidien suspecte".

INTERDITS GENERAUX :
- Ne JAMAIS poser de diagnostic
- Ne JAMAIS recommander un medicament
- Ne JAMAIS inclure d'identifiant cliente dans la sortie
- Ne JAMAIS parler de "budget analyses inclus dans le pack" : il n'y en a pas (cf. modele tarifaire ci-dessous)

REGLE TARIFAIRE :
- Tu proposes les analyses sur PERTINENCE CLINIQUE pure, sans contrainte de
  pack ou de budget. Ne mentionne pas de "budget", "marge", ni de "crédit" dans
  les justifications cliniques.
- Le pack et son prix ne font pas partie de tes donnees d'entree. Le SaaS et
  Anissa gerent separement les droits administratifs et les credits.

REGLE ANTI-SURCHARGE (critique) :
- MAX 3 axes principaux
- MAX 3 analyses proposees
- MAX 3 notes internes (alerts_anissa)
Si plus, FUSIONNER. La force du systeme est sa synthese. Un cockpit
illisible n'est plus un cockpit.

REGLE ANTI-COPIE D EXEMPLE (CRITIQUE) :
- Le schema ci-dessous montre la STRUCTURE attendue avec des placeholders abstraits.
- Tu dois remplir CHAQUE valeur avec les VRAIES donnees de cette cliente extraites
  de l anamnese fournie. JAMAIS de Hashimoto, post-antibiotique, pilule progestative,
  Bristol 1-2, SOPK, chute cheveux, frilosite, etc. si ces elements ne sont pas
  explicitement dans l anamnese de cette cliente.
- Si un champ est vide / inconnu, ecris null ou un texte court neutre. Ne JAMAIS
  inventer ni reutiliser un exemple precedent.

OUTPUT (JSON strict, AUCUN texte autour) — SCHEMA, PAS UN GABARIT A COPIER :
{
  "axes_principaux": [
    "<axe 1 : 3-6 mots cliniques tires de l anamnese reelle>",
    "<axe 2 : optionnel, ordonne par priorite>",
    "<axe 3 : optionnel>"
  ],
  "client_summary": "<2-3 phrases qui prolongent les axes ci-dessus. Vocabulaire prudent. Structure : symptome/signal dominant + antecedent ou facteur de vie pertinent + impact micronutritionnel ou fonctionnel. Reformule avec les VRAIES donnees de la cliente, ne reproduit aucun mot de cet exemple de structure.>",
  "suggestions": [
    {
      "lab_test_code": "<code_du_catalogue>",
      "justification": "<1 phrase ancree dans l anamnese reelle de cette cliente>",
      "pertinence_score": "<1-10>",
      "recommended_default": false
    }
  ],
  "alerts_anissa": [
    "<note interne 1 : signal clinique reel a creuser, ton prudent>",
    "<note interne 2 : optionnel>"
  ]
}

REGLES axes_principaux :
- Tableau de 1 a 3 items maximum
- Chaque axe : 3-6 mots, ton clinique (ex: "Axe digestif fragilise",
  "Carences micronutritionnelles probables", "Stress chronique / axe HPA")
- Ordonner par priorite clinique (le plus urgent en premier)`;
}

/**
 * Build le user prompt avec anamnese pseudonymisee + catalogue.
 */
function buildUserPrompt({ anamnesisPseudo, catalog }) {
  // V97.140 : le labo est desormais expose a l'IA. Tant que le catalogue etait
  // quasi mono-labo (Ortho + 1 test ADN MGD), l'omettre etait sans effet. Avec
  // les bilans sanguins MGD, deux labos proposent des bilans equivalents sous
  // des noms differents : sans le labo ni les marqueurs, l'IA ne peut pas voir
  // qu'elle cumule deux fois la meme analyse.
  const catalogText = catalog
    .filter(t => t.is_active !== false)
    .map(t => `- ${t.code} | ${t.display_name} | labo: ${t.source_lab || 'inconnu'} | ${t.cost_anissa_chf} CHF | ${t.category} | ${t.description || ''}`)
    .join('\n');

  return `ANAMNESE PSEUDONYMISEE (aucune donnee identifiante) :
${JSON.stringify(anamnesisPseudo, null, 2)}

CATALOGUE DISPONIBLE :
${catalogText}

Propose 0 a 3 analyses pertinentes parmi le catalogue ci-dessus, en JSON strict comme specifie dans le system prompt. Privilegie la pertinence au volume.`;
}

/**
 * Appelle Claude pour obtenir des suggestions d'analyses.
 *
 * @param {object} opts
 * @param {object} opts.client    - cliente (avec .form anamnese)
 * @param {string} opts.packType  - code pack (ex: 'suivi_3m_990')
 * @param {Array}  opts.catalog   - tableau de lab_tests depuis Supabase
 * @returns {Promise<object|null>} { client_summary, suggestions, alerts_anissa } ou null
 * @throws {ClaudeApiError} sur erreur HTTP/reseau
 */
export async function suggestAnalyses({ client, packType, catalog }) {
  if (!client) throw new Error('client requis');
  if (!packType) throw new Error('packType requis');
  if (!Array.isArray(catalog) || catalog.length === 0) {
    throw new Error('catalog vide ou invalide');
  }

  const anamnesisPseudo = pseudonymizeAnamnesis(client);
  const system = buildSystemPrompt();
  const user = buildUserPrompt({ anamnesisPseudo, catalog });

  // P0.4 (remède sécurité clinique, 2026-06-10) — depuis l'unification
  // fail-closed de safeParseJson, callClaude({ parseJson }) THROW sur réponse
  // IA vide / tronquée / JSON invalide (au lieu de renvoyer null en silence).
  // La suggestion d'analyses est une feature non critique : « pas de
  // suggestion / réessayez » EST la dégradation honnête. On rattrape donc les
  // erreurs de parsing (taguées parseError) pour préserver le contrat null,
  // tout en laissant remonter les erreurs réseau / HTTP comme avant.
  let result;
  try {
    result = await callClaude({
      system,
      user,
      model: MODEL,
      maxTokens: MAX_TOKENS,
      parseJson: true,
    });
  } catch (err) {
    if (err instanceof ClaudeApiError && err.payload?.parseError) {
      return null;
    }
    throw err;
  }

  // Validation minimale du shape
  if (!result || !Array.isArray(result.suggestions)) {
    return null;
  }

  return result;
}

/**
 * Helper : enrichit les suggestions IA avec les details du catalogue
 * (display_name, cost_anissa_chf, category) pour affichage UI direct.
 *
 * Returns : tableau d'items prets pour la modale
 *   [{ code, display_name, cost_anissa_chf, category, justification, pertinence_score, recommended_default }, ...]
 */
export function enrichSuggestionsWithCatalog(suggestions, catalog) {
  if (!Array.isArray(suggestions)) return [];
  return suggestions
    .map(s => {
      const test = catalog.find(t => t.code === s.lab_test_code);
      if (!test) return null;
      return {
        code: test.code,
        display_name: test.display_name,
        cost_anissa_chf: test.cost_anissa_chf,
        category: test.category,
        source_lab: test.source_lab,
        justification: s.justification || '',
        pertinence_score: s.pertinence_score || 0,
        recommended_default: !!s.recommended_default,
      };
    })
    .filter(Boolean);
}
